import { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { FiInfo } from "react-icons/fi";
import { MdEdit, MdAdd } from "react-icons/md";
import { Trash2, ExternalLink } from "lucide-react";
import { ImCross } from "react-icons/im";
import { useNavigate } from "react-router-dom";
import {
  TAB_OPTIONS,
  WAREHOUSE_COLUMNS,
  WAREHOUSE_COLUMNS_LABELS,
  WAREHOUSE_COLUMN_WIDTHS,
  SITES_COLUMNS,
  SITES_COLUMNS_LABELS,
  TOAST_MESSAGES,
  VALIDATION_MESSAGES,
} from "./constants";
import {
  Spinner,
  GenericTable,
  Pagination,
  ScrollableDropdown,
  FilterCard,
} from "../../Components/Common";
import { extractErrorMessage } from "../../Utils/extractErrorMessage";
import toast from "react-hot-toast";
import {
  getAllWarehouses,
  editWarehouseById,
  createWarehouse,
  deleteWarehouseById,
} from "../../Services/inventoryService";
import { getAllUsers } from "../../Services/userService";
import { SITE_DETAILS } from "../../routes";

const WarehouseMasterList = () => {
  const navigate = useNavigate();

  const [tableLoading, setTableLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(TAB_OPTIONS.WAREHOUSE);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const [warehouses, setWarehouses] = useState([]);
  const [users, setUsers] = useState([]);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [selectedViewWarehouse, setSelectedViewWarehouse] = useState(null);

  const [tempFilters, setTempFilters] = useState({ search: "" });

  const [newWarehouse, setNewWarehouse] = useState({
    name: "",
    address: "",
    person_in_charge_id: "",
  });

  const validationSchema = Yup.object({
    name: Yup.string().required(VALIDATION_MESSAGES.NAME_REQUIRED),
    address: Yup.string().required(VALIDATION_MESSAGES.ADDRESS_REQUIRED),
    person_in_charge_id: Yup.string().required(
      VALIDATION_MESSAGES.PERSON_IN_CHARGE_REQUIRED
    ),
  });

  const handleNavigateToSite = (siteId) => {
    navigate(SITE_DETAILS.replace(":instanceId", siteId));
  };

  const handleViewWarehouse = (warehouse) => {
    setSelectedViewWarehouse(warehouse);
    setIsViewModalOpen(true);
  };

  const handleEditWarehouse = (warehouse) => {
    setSelectedWarehouse(warehouse);
    setIsEditModalOpen(true);
  };

  const handleAddWarehouse = () => {
    setNewWarehouse({ name: "", address: "", person_in_charge_id: null });
    setIsAddModalOpen(true);
  };

  const handleFilterChange = (filters) => {
    setTempFilters(filters);
    setPage(1);
  };

  const fetchUsers = async () => {
    try {
      const res = await getAllUsers({ limit: 0 });
      if (res.status === 200) {
        setUsers(res.data || []);
      } else {
        toast.error("Failed to fetch users");
      }
    } catch (err) {
      toast.error(extractErrorMessage(err) || "Error fetching users");
    }
  };

  const fetchWarehouses = async () => {
    try {
      setTableLoading(true);

      const res = await getAllWarehouses({
        limit: rowsPerPage,
        offset: (page - 1) * rowsPerPage,
        is_site: activeTab === "Sites",
        search: tempFilters.search || "",
      });

      if (res.status === 200) {
        setWarehouses(res.data.results || []);
        setTotalPages(Math.max(1, Math.ceil(res.data.count / rowsPerPage)));
      } else {
        toast.error(TOAST_MESSAGES.FETCH_WAREHOUSE_FAILED);
      }
    } catch (error) {
      const msg =
        extractErrorMessage(error) || TOAST_MESSAGES.FETCH_WAREHOUSE_ERROR;
      toast.error(msg);
    } finally {
      setTableLoading(false);
    }
  };

  const handleSubmitWarehouse = async (values, { setSubmitting }) => {
    setSubmitting(true);
    try {
      setTableLoading(true);
      const res = await createWarehouse(values);
      if (res.status === 201 || res.status === 200) {
        toast.success(TOAST_MESSAGES.ADD_WAREHOUSE_SUCCESS);
        setIsAddModalOpen(false);
        fetchWarehouses();
      } else {
        toast.error(TOAST_MESSAGES.ADD_WAREHOUSE_FAILED);
      }
    } catch (err) {
      toast.error(
        extractErrorMessage(err) || TOAST_MESSAGES.ADD_WAREHOUSE_ERROR
      );
    } finally {
      setTableLoading(false);
      setSubmitting(false);
    }
  };

  const handleUpdateWarehouse = async () => {
    if (!selectedWarehouse) return;

    try {
      setTableLoading(true);

      const payload = {
        name: selectedWarehouse.name,
        address: selectedWarehouse.address,
        person_in_charge_id: selectedWarehouse.person_in_charge_id || null,
      };

      const res = await editWarehouseById(payload, selectedWarehouse.id);

      if (res.status === 200 || res.status === 204) {
        toast.success(TOAST_MESSAGES.UPDATE_WAREHOUSE_SUCCESS);
        setWarehouses((prev) =>
          prev.map((wh) =>
            wh.id === selectedWarehouse.id
              ? {
                  ...wh,
                  ...res.data,
                }
              : wh
          )
        );
        setIsEditModalOpen(false);
        setSelectedWarehouse(null);
      } else {
        toast.error(TOAST_MESSAGES.UPDATE_WAREHOUSE_FAILED);
      }
    } catch (err) {
      const msg =
        extractErrorMessage(err) || TOAST_MESSAGES.UPDATE_WAREHOUSE_ERROR;
      toast.error(msg);
    } finally {
      setTableLoading(false);
    }
  };

  const handleDeleteWarehouse = async (warehouse) => {
    if (!window.confirm("Are you sure you want to delete this warehouse?"))
      return;

    try {
      setTableLoading(true);
      const res = await deleteWarehouseById(warehouse.id);

      if (res.status === 204) {
        toast.success(TOAST_MESSAGES.DELETE_WAREHOUSE_SUCCESS);
        fetchWarehouses(page);
      } else {
        toast.error(TOAST_MESSAGES.DELETE_WAREHOUSE_FAILED);
      }
    } catch (err) {
      const msg =
        extractErrorMessage(err) || TOAST_MESSAGES.DELETE_WAREHOUSE_ERROR;
      toast.error(msg);
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    fetchWarehouses();
  }, [page, rowsPerPage, activeTab, tempFilters.search]);

  useEffect(() => {
    fetchUsers();
  }, []);

  const renderMobileFields = (row) => {
    return (
      <div
        key={row.id}
        className="border-b-2 rounded-md p-3 mb-3 bg-white hover:bg-gray-50 transition-all duration-200"
        onClick={() => handleViewWarehouse(row)}
      >
        <div className="grid grid-cols-4 items-start gap-2">
          <div className="col-span-2 flex flex-col">
            <div className="font-semibold text-gray-900 text-base truncate max-w-[90%]">
              {row.name}
            </div>
            {activeTab === TAB_OPTIONS.SITES && (
              <div className="text-gray-600 text-sm truncate max-w-[90%]">
                {row.project_name || "-"}
              </div>
            )}

            <div className="text-gray-600 text-sm truncate max-w-[90%]">
              {row.address || "-"}
            </div>
          </div>

          {activeTab === TAB_OPTIONS.WAREHOUSE ? (
            <div className="col-span-1 text-gray-700 text-sm flex items-center justify-center text-center truncate">
              {row.person_in_charge_name || "-"}
            </div>
          ) : (
            <div className="col-span-1"></div>
          )}

          <div
            className="col-span-1 flex justify-center items-center gap-3"
            onClick={(e) => e.stopPropagation()}
          >
            {activeTab === TAB_OPTIONS.WAREHOUSE && (
              <>
                <MdEdit
                  size={18}
                  className="text-primary cursor-pointer"
                  onClick={() => handleEditWarehouse(row)}
                />
                <Trash2
                  size={18}
                  className="text-red-600 cursor-pointer"
                  onClick={() => handleDeleteWarehouse(row)}
                />
              </>
            )}
            {activeTab === TAB_OPTIONS.SITES && (
              <ExternalLink
                size={18}
                className="text-blue-500 hover:text-blue-800 cursor-pointer"
                onClick={() => handleNavigateToSite(row.site)}
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderField = (key, row) => {
    const content = (() => {
      switch (key) {
        case "name":
          return <span className="font-medium text-gray-900">{row.name}</span>;
        case "project_name":
          return <span>{row.project_name || "-"}</span>;
        case "address":
          return <span>{row.address || "-"}</span>;
        case "person_in_charge":
          if (activeTab === TAB_OPTIONS.WAREHOUSE) {
            return <span>{row.person_in_charge_name || "-"}</span>;
          }
          return null;
        case "actions":
          if (activeTab === TAB_OPTIONS.WAREHOUSE) {
            return (
              <div className="flex gap-6 justify-center items-center">
                <FiInfo
                  size={20}
                  onClick={() => handleViewWarehouse(row)}
                  className="text-blue-600 cursor-pointer"
                />
                <MdEdit
                  size={20}
                  onClick={() => handleEditWarehouse(row)}
                  className="text-primary cursor-pointer"
                />
                <Trash2
                  size={20}
                  onClick={() => handleDeleteWarehouse(row)}
                  className="text-red-600 cursor-pointer"
                />
              </div>
            );
          }

          if (activeTab === TAB_OPTIONS.SITES) {
            return (
              <div className="flex justify-start items-center ml-3">
                <ExternalLink
                  size={20}
                  className="text-blue-500 hover:text-blue-800 cursor-pointer"
                  onClick={() => handleNavigateToSite(row.site)}
                />
              </div>
            );
          }

          return null;

        default:
          return row[key] ?? "-";
      }
    })();

    return (
      <div className={`${WAREHOUSE_COLUMN_WIDTHS[key] || ""}`}>{content}</div>
    );
  };

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between md:gap-4 gap-2">
        <div className="flex-1 pl-6">
          <h1 className="text-black text-xl md:text-2xl font-semibold pt-2 md:py-3 whitespace-nowrap">
            Warehouse Master
          </h1>
        </div>

        <button
          onClick={handleAddWarehouse}
          className={`px-4 py-2 gap-2 rounded-md flex items-center bg-primary text-white hover:bg-blue-700 m-5 mb-3`}
          aria-label="Add Announcement"
        >
          <MdAdd size={18} />
          <span className="hidden md:inline md:whitespace-nowrap text-sm md:text-base">
            Add Warehouse
          </span>
        </button>
      </div>

      {/* Filtercard */}
      <div className="mb-3 mx-2 mt-0">
        <FilterCard
          tempFilters={tempFilters}
          setTempFilters={setTempFilters}
          onFilterChange={handleFilterChange}
          fields={[
            {
              key: "search",
              type: "search",
              label:
                activeTab === TAB_OPTIONS.SITES
                  ? "Search Site"
                  : "Search Warehouse",
              placeholder:
                activeTab === TAB_OPTIONS.SITES
                  ? "Search by site name..."
                  : "Search by warehouse name...",
            },
          ]}
        />
      </div>

      {/* Tabs */}
      <div className="flex justify-start items-center gap-4 pb-1 px-2 md:px-4">
        {Object.values(TAB_OPTIONS).map((tab) => (
          <button
            key={tab}
            onClick={() => {
              setActiveTab(tab);
              setPage(1);
            }}
            className={`px-4 py-1 font-bold border-b-2 text-sm md:text-base ${
              activeTab === tab
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-600 hover:text-gray-800"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === TAB_OPTIONS.WAREHOUSE && (
        <div className="flex-1 overflow-x-auto sm:rounded-lg mt-2">
          {tableLoading ? (
            <div className="flex items-center justify-center h-[60vh]">
              <Spinner />
            </div>
          ) : (
            <div className="w-full flex flex-col h-[61vh] md:h-[43vh] overflow-x-auto px-1">
              <GenericTable
                tableColumns={WAREHOUSE_COLUMNS}
                tableRows={warehouses}
                columnLabels={WAREHOUSE_COLUMNS_LABELS}
                renderField={renderField}
                enableGridLayout={true}
                mobileGridColumns={3}
                renderMobileCard={renderMobileFields}
              />
            </div>
          )}

          {warehouses.length > 0 && (
            <div className="flex justify-end pb-4 px-4 mt-2">
              <Pagination
                page={page}
                totalPages={totalPages}
                rowsPerPage={rowsPerPage}
                onPageChange={setPage}
                onPageSizeChange={setRowsPerPage}
              />
            </div>
          )}
        </div>
      )}

      {activeTab === TAB_OPTIONS.SITES && (
        <div className="flex-1 overflow-x-auto sm:rounded-lg mt-2">
          {tableLoading ? (
            <div className="flex items-center justify-center h-[60vh]">
              <Spinner />
            </div>
          ) : (
            <div className="w-full flex flex-col h-[61vh] md:h-[43vh] overflow-x-auto px-1">
              <GenericTable
                tableColumns={SITES_COLUMNS}
                tableRows={warehouses}
                columnLabels={SITES_COLUMNS_LABELS}
                renderField={renderField}
                enableGridLayout={true}
                mobileGridColumns={3}
                renderMobileCard={renderMobileFields}
              />
            </div>
          )}

          {warehouses.length > 0 && (
            <div className="flex justify-end pb-4 px-4 mt-2">
              <Pagination
                page={page}
                totalPages={totalPages}
                rowsPerPage={rowsPerPage}
                onPageChange={setPage}
                onPageSizeChange={setRowsPerPage}
              />
            </div>
          )}
        </div>
      )}

      {/* View Modal */}
      {isViewModalOpen && selectedViewWarehouse && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-3">
          <div className="bg-white rounded-xl shadow-xl p-3 w-full max-w-2xl relative">
            <button
              onClick={() => setIsViewModalOpen(false)}
              className="absolute top-3 right-3 w-8 h-6 flex items-center justify-center rounded-sm bg-red-500 text-gray-700 font-bold hover:bg-red-700 hover:text-gray-800"
            >
              <ImCross size={10} />
            </button>

            <div className="px-6 py-2 border-b">
              <h2 className="text-2xl font-bold">Warehouse Details</h2>
            </div>

            <div className="px-6 py-4 space-y-4">
              <div>
                <h3 className="text-lg font-semibold mb-1">Name</h3>
                <p className="text-gray-700">
                  {selectedViewWarehouse.name || "-"}
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-1">Address</h3>
                <p className="text-gray-700">
                  {selectedViewWarehouse.address || "-"}
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-1">Person In Charge</h3>
                <p className="text-gray-700">
                  {selectedViewWarehouse.person_in_charge_name || "-"}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {isEditModalOpen && selectedWarehouse && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-3">
          <div className="bg-white rounded-xl shadow-xl p-4 w-full max-w-2xl relative">
            <button
              onClick={() => setIsEditModalOpen(false)}
              className="absolute top-3 right-3 w-8 h-6 flex items-center justify-center rounded-sm bg-red-500 text-gray-700 font-bold hover:bg-red-700 hover:text-gray-800"
            >
              <ImCross size={10} />
            </button>

            <div className="px-6 py-2 border-b">
              <h2 className="text-2xl font-bold">Edit Warehouse</h2>
            </div>

            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block mb-1 font-medium text-gray-700">
                  Name
                </label>
                <input
                  type="text"
                  value={selectedWarehouse.name}
                  onChange={(e) =>
                    setSelectedWarehouse({
                      ...selectedWarehouse,
                      name: e.target.value,
                    })
                  }
                  className="border border-gray-300 rounded-lg p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>

              <div>
                <label className="block mb-1 font-medium text-gray-700">
                  Address
                </label>
                <textarea
                  value={selectedWarehouse.address || ""}
                  onChange={(e) =>
                    setSelectedWarehouse({
                      ...selectedWarehouse,
                      address: e.target.value,
                    })
                  }
                  className="border border-gray-300 rounded-lg p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                  rows={2}
                />
              </div>

              <div>
                <label className="block mb-1 font-medium text-gray-700">
                  Person In Charge
                </label>

                <ScrollableDropdown
                  label="Select User"
                  options={users.map((user) => ({
                    value: user.id,
                    label: `${user.first_name} ${user.last_name}`,
                  }))}
                  value={selectedWarehouse.person_in_charge_id || ""}
                  onChange={(val) =>
                    setSelectedWarehouse({
                      ...selectedWarehouse,
                      person_in_charge_id: val,
                    })
                  }
                  isOpen={isUserDropdownOpen}
                  onToggle={setIsUserDropdownOpen}
                />
              </div>
            </div>

            <div className="px-6 py-4 flex justify-end gap-3">
              <button
                onClick={handleUpdateWarehouse}
                className="bg-primary hover:bg-blue-900 text-white px-5 py-2 rounded-lg font-medium"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-3">
          <div className="bg-white rounded-xl shadow-xl p-4 w-full max-w-2xl relative">
            <button
              onClick={() => setIsAddModalOpen(false)}
              className="absolute top-3 right-3 w-8 h-6 flex items-center justify-center rounded-sm bg-red-500 text-gray-700 font-bold hover:bg-red-700 hover:text-gray-800"
            >
              <ImCross size={10} />
            </button>
            <h2 className="text-2xl font-bold mb-3">Add Warehouse</h2>

            <Formik
              initialValues={newWarehouse}
              validationSchema={validationSchema}
              onSubmit={handleSubmitWarehouse}
            >
              {({ isSubmitting, setFieldValue, values }) => (
                <Form className="px-6 py-2 space-y-3">
                  <div>
                    <label className="block mb-1 font-medium">
                      Warehouse Name <span className="text-red-500">*</span>
                    </label>
                    <Field
                      name="name"
                      type="text"
                      className="border rounded-lg p-2 w-full"
                      placeholder="Enter warehouse name"
                    />
                    <ErrorMessage
                      name="name"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>

                  <div>
                    <label className="block mb-1 font-medium">
                      Address <span className="text-red-500">*</span>
                    </label>
                    <Field
                      as="textarea"
                      name="address"
                      rows={2}
                      className="border rounded-lg p-2 w-full"
                      placeholder="Enter address"
                    />
                    <ErrorMessage
                      name="address"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>

                  <div>
                    <label className="block mb-1 font-medium">
                      Person In Charge <span className="text-red-500">*</span>
                    </label>
                    <ScrollableDropdown
                      label="Select User"
                      options={users.map((user) => ({
                        value: user.id,
                        label: `${user.first_name} ${user.last_name}`,
                      }))}
                      value={values.person_in_charge_id || ""}
                      onChange={(val) =>
                        setFieldValue("person_in_charge_id", val)
                      }
                      isOpen={isUserDropdownOpen}
                      onToggle={setIsUserDropdownOpen}
                    />
                    <ErrorMessage
                      name="person_in_charge_id"
                      component="div"
                      className="text-red-500 text-xs mt-[2px]"
                    />
                  </div>

                  <div className="flex justify-end gap-3 pt-3">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="bg-primary text-white px-5 py-2 rounded-lg"
                    >
                      {isSubmitting ? <Spinner /> : "Save"}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      )}
    </div>
  );
};

export default WarehouseMasterList;
